import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { FormSpy } from 'react-final-form';
import { ARRAY_ERROR, FormState } from 'final-form';

import { IDictionaryChecklistSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryChecklistSelectOption';
import { newChecklistSelectOption } from 'KnmErknmPackage/MainModule/constants/newChecklistSelectOption';
import { IKnmErknmFormChecklist } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChecklist';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { getChecklistBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmErknmChecklistsSelectOptions,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsNew,
  getKnmDataCreated,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IChecklistDictionary } from 'APIPackage/DictionariesModule/interfaces/IChecklistDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { CHECLISTS_BLOCK_ID } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ErknmApproveRemarkSectionCodeEnum } from 'KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { getUserRoleIsProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';

import { ErknmChecklist } from './ErknmChecklist';
import styles from './ErknmChecklists.module.scss';
import { ErknmFillInProcessMessage } from '../../../MainModule/components/ErknmFillInProcessMessage';
import { isLaterThanAdminSetting } from '../../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { getKnmErknmInitialFormValues } from '../../../MainModule/selectors/knmErknmSelectors';
import { KnmKindCodesEnum } from '../../../../APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { ErknmPlanStatusesEnum } from '../../../../APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { ErknmFieldMark } from '../../../MainModule/components/ErknmFieldMark';
import { ErknmApproveRemarkAddButton } from '../../../ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { ErknmApproveRemark } from '../../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { ErknmApproveRemarkChanges } from '../../../ErknmApproveRemarkModule/components/ErknmApproveRemarkChanges';
import { useIsChecklistEnabled } from '../../../MainModule/fieldsAvailability/erknmChecklistAvailability';
import { checkHasErrorInCollapseBlock } from '../../../../functions/checkHasErrorInCollapseBlock';
import { ErknmChecklistRemarkChanges } from './ErknmChecklistRemarkChanges';

const COLLAPSE_TITLE_BASE = 'Проверочный лист';

interface IErknmChecklistsProps {
  isChecklistsUsed: boolean | undefined;
  kindKnmCode: string | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
}

interface IErknmChecklistsStoreProps {
  basicChecklistsSelectOptions: IDictionarySelectOption<IChecklistDictionary>[] | undefined;
  created: string | null | undefined;
  formRef: IKnmFormRef | undefined;
  initialFormValues: IKnmErknmForm;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isNewKnm: boolean;
  settings: IAdminSetting[] | undefined;
  isProsecutor?: boolean;
}

const ErknmChecklistsWithoutProps = ({
  isChecklistsUsed,
  kindKnmCode,
  knmPlanStatus,
  basicChecklistsSelectOptions,
  created,
  formRef,
  initialFormValues,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isNewKnm,
  settings,
  isProsecutor,
}: IErknmChecklistsProps & IErknmChecklistsStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isEnabled = useIsChecklistEnabled();

  const checklistsSelectOptions: IDictionaryChecklistSelectOption[] | undefined = useMemo(() => {
    return [...(basicChecklistsSelectOptions || []), newChecklistSelectOption];
  }, [basicChecklistsSelectOptions]);

  const addField = useCallback(() => {
    if (isEnabled && push) {
      const newChecklist: IKnmErknmFormChecklist = {
        listId: uuidv4(),
        isNew: true,
      };

      push('checklistsErknm', newChecklist);
    }
  }, [push, isEnabled]);

  const makeRemoveField = (index: number) =>
    isEnabled && !isProsecutor && remove ? () => remove('checklistsErknm', index) : undefined;

  const validateChecklistsErknm = useCallback(
    (values: IKnmErknmFormChecklist[] | undefined) => {
      if ((isKnmStatusFillInProcess || isKnmStatusReadyForApprove) && !values?.length) {
        return REQUIRED_ERROR;
      }
    },
    [isKnmStatusFillInProcess, isKnmStatusReadyForApprove],
  );

  const isCheck4155Validation = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_55_STARTS_FROM,
  );

  const isCheckListUsedWorkingKnmStatuses =
    (!knmPlanStatus && (kindKnmCode === KnmKindCodesEnum.RO || kindKnmCode === KnmKindCodesEnum.VP)) ||
    kindKnmCode === KnmKindCodesEnum.IV;

  const isChecklistsUsedDisabled =
    initialFormValues.isChecklistsUsed && isCheck4155Validation && !isCheckListUsedWorkingKnmStatuses;

  useEffect(() => {
    if (!isChecklistsUsed && isNewKnm && isCheck4155Validation) {
      formRef?.current.change('isChecklistsUsed', true);
    }
  }, [formRef, isNewKnm, isChecklistsUsed, isCheck4155Validation]);

  const handleChecklistsUsedChange = (v: ChangeEvent<HTMLInputElement>) => {
    if (!v.target.checked) {
      formRef?.current.change('checklistsErknm');
    }
  };

  return (
    <FieldBlock fillInProcess={<ErknmFillInProcessMessage name="isChecklistsUsed" />}>
      <Row>
        <CheckboxField
          className={styles.IsUsedField}
          labelClassName="shared-field-label_user-select"
          id="isChecklistsUsed"
          name="isChecklistsUsed"
          label="Отметка об использовании проверочного листа"
          size="medium"
          type="checkbox"
          theme="system"
          disabled={!isEnabled || isChecklistsUsedDisabled}
          onChange={handleChecklistsUsedChange}
        />
      </Row>

      {isChecklistsUsed && (
        <Row>
          <FieldBlock
            label={
              <>
                Проверочные листы
                <ErknmFieldMark fieldName="checklistsErknm" />
              </>
            }
            id={CHECLISTS_BLOCK_ID}
            button={
              <>
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  variant="link"
                  color="primary"
                  type="button"
                  disabled={!isEnabled}
                  onClick={addField}
                />
                <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.CHECKLISTS} />
              </>
            }
            fillInProcess={<ErknmFillInProcessMessage name="checklistsErknm" />}
          >
            <FormSpy subscription={{ errors: true, touched: true }}>
              {(props: FormState<IKnmErknmForm>) => {
                const legalBasesFieldError = props.errors?.checklistsErknm?.[ARRAY_ERROR];
                const isLegalBasesFieldTouched = props.touched?.checklistsErknm;

                if (legalBasesFieldError && isLegalBasesFieldTouched) {
                  return <p className="shared-input-error">{legalBasesFieldError}</p>;
                }

                return null;
              }}
            </FormSpy>
            <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.CHECKLISTS} />

            <FieldArray name="checklistsErknm" validate={validateChecklistsErknm}>
              {({ fields }: FieldArrayRenderProps<IKnmErknmFormChecklist, HTMLElement>) => {
                if (!fields.length) {
                  return null;
                }

                return (
                  <ul className={styles.ChecklistsList}>
                    {fields.map((checklistFieldName, index) => {
                      const fieldValue = fields.value?.[index];
                      const id = getChecklistBlockId(fieldValue.listId);

                      const hasError = checkHasErrorInCollapseBlock(formRef, checklistFieldName);

                      return (
                        <li className={styles.ChecklistsListItem} key={id} id={id}>
                          <KnmCollapse
                            theme="fields"
                            title={fieldValue?.isNew ? COLLAPSE_TITLE_BASE : `${COLLAPSE_TITLE_BASE} №${index + 1}`}
                            isNew={fieldValue?.isNew || fieldValue?.uploadedViaSmev}
                            isNewTitle={fieldValue?.uploadedViaSmev ? 'Загружен через СМЭВ' : undefined}
                            openByDefault={fieldValue?.isNew}
                            onClose={makeRemoveField(index)}
                            additionalButtons={
                              <>
                                <ErknmApproveRemarkChanges
                                  sectionCode={ErknmApproveRemarkSectionCodeEnum.CHECKLISTS}
                                  guid={fieldValue.guid}
                                />
                                {isProsecutor && (
                                  <ErknmChecklistRemarkChanges
                                    checkGuid={fieldValue.guid}
                                    checklistGuid={fieldValue.checkList?.value.dictVersionId}
                                    numberCheckList={fieldValue.ervkCheckList?.number}
                                  />
                                )}
                              </>
                            }
                            changeOpenedCallback={e =>
                              setKnmCollapseOpened(state => ({
                                ...state,
                                [checklistFieldName]: e,
                              }))
                            }
                            error={hasError && knmCollapseOpened[checklistFieldName]}
                          >
                            <ErknmChecklist
                              watchOnly={!isEnabled}
                              formRef={formRef}
                              fieldName={checklistFieldName}
                              data={fieldValue}
                              checklistsSelectOptions={checklistsSelectOptions}
                            />
                          </KnmCollapse>
                        </li>
                      );
                    })}
                  </ul>
                );
              }}
            </FieldArray>
          </FieldBlock>
        </Row>
      )}
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState) => ({
  basicChecklistsSelectOptions: getKnmErknmChecklistsSelectOptions(state),
  created: getKnmDataCreated(state),
  formRef: getKnmFormRef(state),
  initialFormValues: getKnmErknmInitialFormValues(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isNewKnm: getKnmIsNew(state),
  settings: getAdminFetchSettings(state),
  isProsecutor: getUserRoleIsProsecutor(state),
});

export const ErknmChecklists = connect(mapStateToProps)(ErknmChecklistsWithoutProps);
